import { Link } from 'react-router-dom';
import { useHomepageSection } from '@/hooks/useHomepageSection';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import { CONTACT_COPY, SITE } from '@/content/yawasCopy';
import { DEFAULT_RESTAURANT_HOURS, parseOpeningHours } from '@/content/openingHours';
import OpeningHoursList from '@/components/feature/OpeningHoursList';

const ContactSection = () => {
  const { data } = useHomepageSection('contact');
  const { get } = useSiteSettings();

  const title = data?.title?.trim() || CONTACT_COPY.title;
  const subtitle = data?.subtitle?.trim() || CONTACT_COPY.subtitle;
  const description = data?.description?.trim() || CONTACT_COPY.description;

  const phone = get('restaurant_phone', SITE.phoneDisplay) || SITE.phoneDisplay;
  const email = get('restaurant_email', SITE.email) || SITE.email;
  const mapsQuery = SITE.addressOneLine;
  const openingHours = parseOpeningHours(get('restaurant_hours', DEFAULT_RESTAURANT_HOURS));

  return (
    <section id="contact" className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <span className="text-amber-500 text-sm font-semibold tracking-widest uppercase">{subtitle}</span>
          <h2
            className="text-3xl md:text-5xl font-bold text-stone-900 mt-3"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {title}
          </h2>
          <p className="text-stone-500 mt-4 max-w-xl mx-auto text-base">{description}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          <div className="flex flex-col gap-6">
            <div className="flex items-start gap-5 p-6 rounded-2xl bg-stone-50 border border-stone-100">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-amber-100 text-amber-600 flex-shrink-0">
                <i className="ri-store-2-line text-xl"></i>
              </div>
              <div>
                <p className="text-xs font-semibold text-stone-400 uppercase tracking-wide mb-1">Restaurant</p>
                <p className="text-stone-900 font-semibold text-lg">{SITE.name}</p>
              </div>
            </div>

            <a
              href={`tel:${SITE.phoneTel}`}
              className="flex items-start gap-5 p-6 rounded-2xl bg-stone-50 border border-stone-100 hover:border-amber-200 hover:bg-amber-50/30 transition-all group cursor-pointer"
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-amber-100 text-amber-600 flex-shrink-0 group-hover:bg-amber-500 group-hover:text-white transition-all">
                <i className="ri-phone-line text-xl"></i>
              </div>
              <div>
                <p className="text-xs font-semibold text-stone-400 uppercase tracking-wide mb-1">Telefon</p>
                <p className="text-stone-900 font-semibold text-lg">{phone}</p>
                <p className="text-stone-500 text-sm mt-0.5">{CONTACT_COPY.callCta}</p>
              </div>
            </a>

            <a
              href={`mailto:${email}`}
              className="flex items-start gap-5 p-6 rounded-2xl bg-stone-50 border border-stone-100 hover:border-amber-200 hover:bg-amber-50/30 transition-all group cursor-pointer"
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-amber-100 text-amber-600 flex-shrink-0 group-hover:bg-amber-500 group-hover:text-white transition-all">
                <i className="ri-mail-line text-xl"></i>
              </div>
              <div>
                <p className="text-xs font-semibold text-stone-400 uppercase tracking-wide mb-1">E-Mail</p>
                <p className="text-stone-900 font-semibold text-lg">{email}</p>
              </div>
            </a>

            <div className="flex items-start gap-5 p-6 rounded-2xl bg-stone-50 border border-stone-100">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-amber-100 text-amber-600 flex-shrink-0">
                <i className="ri-map-pin-line text-xl"></i>
              </div>
              <div>
                <p className="text-xs font-semibold text-stone-400 uppercase tracking-wide mb-1">Adresse</p>
                <p className="text-stone-900 font-semibold text-lg whitespace-pre-line">
                  {SITE.addressLines.join('\n')}
                </p>
                <a
                  href={`https://maps.google.com/?q=${encodeURIComponent(mapsQuery)}`}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="text-amber-500 text-sm mt-0.5 hover:underline inline-block"
                >
                  {CONTACT_COPY.routeCta} →
                </a>
              </div>
            </div>

            <div className="p-6 rounded-2xl bg-stone-50 border border-stone-100">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-amber-100 text-amber-600 flex-shrink-0">
                  <i className="ri-time-line text-xl"></i>
                </div>
                <div>
                  <p className="text-xs font-semibold text-stone-400 uppercase tracking-wide mb-0.5">Öffnungszeiten</p>
                </div>
              </div>
              <div className="space-y-2 pl-0">
                <OpeningHoursList rows={openingHours} />
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-6">
            <div className="w-full h-80 md:h-96 rounded-2xl overflow-hidden border border-stone-100">
              <iframe
                title="Yawas Kebab Standort St. Gallen"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2698.5628302852065!2d9.4191868!3d47.43996719999999!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x479b1f1ef10bb6ef%3A0x2a8a4af781aadc34!2sYa%20was%20Take-Away!5e0!3m2!1sde!2sch!4v1785791941084!5m2!1sde!2sch&hl=de"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="strict-origin-when-cross-origin"
              />
            </div>

            <div className="p-6 rounded-2xl bg-amber-500 text-white">
              <h3 className="text-xl font-bold mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                {CONTACT_COPY.boxTitle}
              </h3>
              <p className="text-amber-100 text-sm mb-4">{CONTACT_COPY.boxText}</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href={`https://maps.google.com/?q=${encodeURIComponent(mapsQuery)}`}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-white text-amber-600 font-semibold rounded-full text-sm hover:bg-amber-50 transition-all cursor-pointer whitespace-nowrap"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-map-pin-line"></i>
                  </div>
                  {CONTACT_COPY.routeCta}
                </a>
                <a
                  href={`tel:${SITE.phoneTel}`}
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-stone-900 text-white font-semibold rounded-full text-sm hover:bg-stone-800 transition-all cursor-pointer whitespace-nowrap"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-phone-line"></i>
                  </div>
                  {CONTACT_COPY.callCta}
                </a>
              </div>
              <Link
                to="/menu"
                className="mt-3 inline-flex items-center gap-1 text-amber-100 text-sm hover:text-white transition-colors"
              >
                Speisekarte entdecken
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
